import styled, { css } from 'styled-components';
import CardBlock from './card-block';

const CardHeaderTextContent = styled.div`
	flex: 1 1 auto;
	overflow: hidden;
`;

const CardHeaderActionContent = styled.div``;

const ellipsisStyle = css`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

interface StyleProps {
	ellipsis?: boolean;
}

const CardHeaderTitle = styled.div<StyleProps>`
	font: var(--card-header-title-font, var(--label-bold));
	color: var(--card-header-title-font-color);
	text-transform: var(--brand-text-transform, none);
	${({ ellipsis }) => ellipsis && ellipsisStyle}
`;

const CardHeaderCaption = styled.div<StyleProps>`
	font: var(--card-header-caption-font, var(--caption));
	color: var(--card-header-caption-font-color, rgb(var(--base-06)));
	${({ ellipsis }) => ellipsis && ellipsisStyle}
`;

const BasicCardBlockTitle = styled.p<StyleProps>`
	color: var(--card-header-title-font-color, rgb(var(--base-02)));
	font: var(--caption);
	align-self: center;
	${({ ellipsis }) => ellipsis && ellipsisStyle}
`;

const BasicCardBlockTitleWithAction = styled.a<StyleProps>`
	color: var(--card-header-title-font-color, rgb(var(--base-02)));
	font: var(--caption);
	line-height: 30px;
	padding-left: 8px;
	${({ ellipsis }) => ellipsis && ellipsisStyle}
`;

const BasicCardBlock = styled(CardBlock)`
	height: 30px;
	max-height: 30px;
	width: 100%;
	border-radius: 0 0 3px 3px;
	align-items: center;
	justify-content: space-between;
`;

interface CardHeaderProps {
	title: string;
	caption?: string;
	actionContent?: React.ReactNode;
	headerTitleEllipsis?: boolean;
	headerCaptionEllipsis?: boolean;
}

/**
 * A `CardHeader` is a component that contains the title and caption of a card
 *
 * It might also contain an actionContent if you want to prompt other options on the card
 */
const CardHeader: React.FC<CardHeaderProps> = ({
	title,
	caption,
	actionContent,
	headerTitleEllipsis,
	headerCaptionEllipsis
}) => (
	<CardBlock>
		<CardHeaderTextContent>
			<CardHeaderCaption ellipsis={headerCaptionEllipsis}>{caption}</CardHeaderCaption>
			<CardHeaderTitle ellipsis={headerTitleEllipsis}>{title}</CardHeaderTitle>
		</CardHeaderTextContent>
		{actionContent && <CardHeaderActionContent>{actionContent}</CardHeaderActionContent>}
	</CardBlock>
);

interface BasicCardHeaderProps {
	title: string;
	actionContent?: React.ReactNode;
	padding?: string;
	titleAction?: () => void;
	headerTitleEllipsis?: boolean;
}

export const BasicCardHeader: React.FC<BasicCardHeaderProps> = ({
	title,
	actionContent,
	padding = '8px',
	titleAction,
	headerTitleEllipsis
}) => (
	<BasicCardBlock padding={padding}>
		{titleAction ? (
			<BasicCardBlockTitleWithAction onClick={titleAction} ellipsis={headerTitleEllipsis}>
				{title}
			</BasicCardBlockTitleWithAction>
		) : (
			<BasicCardBlockTitle ellipsis={headerTitleEllipsis}>{title}</BasicCardBlockTitle>
		)}
		{actionContent && actionContent}
	</BasicCardBlock>
);

export default CardHeader;
